import { useFeatureFlags } from "@bcc/hooks/use-feature-flags";
import { useSFDCAuthState } from "@client-shared/providers/auth/useSFDCAuthState";
import { useVendorContext } from "@client-shared/providers/vendor";
import type { FeatureFlags } from "@shared/types";
import { createContext, useContext, useMemo, type ReactNode } from "react";

export interface FeatureFlagsContextValue {
  readonly flags: FeatureFlags;
  readonly isFeatureFlagsLoading: boolean;
  readonly isFeatureEnabled: (flagName: string) => boolean;
}

const defaultContextValue: FeatureFlagsContextValue = {
  flags: {},
  isFeatureFlagsLoading: false,
  isFeatureEnabled: () => false,
};

export const FeatureFlagsContext = createContext<FeatureFlagsContextValue>(defaultContextValue);

export const useFeatureFlagsContext = (): FeatureFlagsContextValue => useContext(FeatureFlagsContext);

export const FeatureFlagsProvider = ({ children }: { children: ReactNode }) => {
  const [{ currentVendor }] = useVendorContext();
  const [{ sfdcAuthToken }] = useSFDCAuthState();
  const { flags, isFeatureFlagsLoading } = useFeatureFlags(currentVendor?.region, currentVendor?.countryCode, Boolean(sfdcAuthToken));

  const value = useMemo<FeatureFlagsContextValue>(
    () => ({
      flags,
      isFeatureFlagsLoading,
      isFeatureEnabled: (flagName: string) => flags[flagName] ?? false,
    }),
    [flags, isFeatureFlagsLoading]
  );

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
};
